import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Box,
  Grid,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS as dateFnsEN } from 'date-fns/locale/en-US';
import { tr as dateFnsTR } from 'date-fns/locale/tr';
import { getI18n, useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PhoneInput, { parsePhoneNumber } from './PhoneInput';
import { saveCallBackForm } from '../api';
import { AuthContext } from '../context/AuthContext';
import { ICallBackFormInput } from '../utils/types';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

interface CallBackDialogProps {
  open: boolean;
  onClose: () => void;
  tourId: number;
}

export const CallBackFormDialog: React.FC<CallBackDialogProps> = ({
  open,
  onClose,
  tourId,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const i18n = getI18n();
  const dateFnsLocale = i18n.language === 'tr' ? dateFnsTR : dateFnsEN;
  const { token } = useContext(AuthContext);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [personCount, setPersonCount] = useState(1);
  const showSnackbar = useAlertSnackbar();
  const [callOption, setCallOption] = useState<'doesNotMatter' | 'selectDay'>(
    'doesNotMatter',
  );

  const [isEmailValid, setIsEmailValid] = useState(true);
  const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  useEffect(() => {
    setIsEmailValid(email === '' || emailPattern.test(email));
  }, [email]);

  const handleSave = async () => {
    const payload: ICallBackFormInput = {
      customerFullName: fullName,
      description,
      desiredContactDate: selectedDate?.toISOString(),
      desiredContactTime: selectedTime,
      electronicAddress: {
        electronicAddress: email,
        typeId: 6,
      },
      phoneNumber: {
        areaCode: null,
        countryCode: phoneCountryCode,
        phoneNumber,
        typeId: 2,
      },
      personCount,
      tourId: tourId,
    };

    try {
      await saveCallBackForm(token, payload);
      showSnackbar(t('callBackFormDialog.snackbarSuccess'), 'success');
      handleCloseDialog();
    } catch (err: any) {
      showSnackbar(
        err.detail || t('callBackFormDialog.snackbarError'),
        'error',
      );
    }
  };

  const isFormValid =
    fullName.trim() !== '' &&
    email.trim() !== '' &&
    isEmailValid &&
    phoneNumber.trim() !== '' &&
    personCount > 0 &&
    (callOption === 'doesNotMatter' ||
      (selectedDate !== null && selectedTime !== ''));

  const resetForm = () => {
    setSelectedDate(null);
    setFullName('');
    setEmail('');
    setPhoneCountryCode('90');
    setPhoneNumber('');
    setDescription('');
    setSelectedTime('');
    setPersonCount(1);
    setCallOption('doesNotMatter');
  };

  const handleCloseDialog = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
      <DialogTitle>{t('callBackFormDialog.title')}</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label={t('callBackFormDialog.fullName')}
          fullWidth
          value={fullName}
          required
          onChange={(e) => setFullName(e.target.value)}
        />
        <TextField
          margin="dense"
          label={t('callBackFormDialog.email')}
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!isEmailValid}
          helperText={!isEmailValid && t('callBackFormDialog.invalidEmail')}
        />

        <PhoneInput
          label={t('callBackFormDialog.phone')}
          value={
            phoneCountryCode && phoneNumber
              ? `+${phoneCountryCode}${phoneNumber}`
              : ''
          }
          onChange={(data) => {
            const parsed = parsePhoneNumber(data || '');
            if (!parsed) return;
            setPhoneCountryCode(parsed.countryCallingCode);
            setPhoneNumber(parsed.nationalNumber);
          }}
          required
        />

        <Box mt={2}>
          <FormLabel>{t('callBackFormDialog.callOptionLabel')}</FormLabel>
          <RadioGroup
            row
            value={callOption}
            onChange={(e) =>
              setCallOption(e.target.value as 'doesNotMatter' | 'selectDay')
            }
          >
            <FormControlLabel
              value="doesNotMatter"
              control={<Radio />}
              label={t('callBackFormDialog.callOption.doesNotMatter')}
            />
            <FormControlLabel
              value="selectDay"
              control={<Radio />}
              label={t('callBackFormDialog.callOption.selectDay')}
            />
          </RadioGroup>
        </Box>

        {callOption === 'selectDay' && (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateFnsLocale}
          >
            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <DatePicker
                  label={t('callBackFormDialog.dateLabel')}
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  format="dd.MM.yyyy"
                  slotProps={{ textField: { fullWidth: true } }}
                  minDate={new Date()}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="time"
                  label={t('callBackFormDialog.timeLabel')}
                  fullWidth
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        )}

        <TextField
          margin="dense"
          label={t('callBackFormDialog.personCount')}
          fullWidth
          type="number"
          value={personCount}
          onChange={(e) => setPersonCount(Number(e.target.value))}
        />

        <TextField
          margin="dense"
          label={t('callBackFormDialog.description')}
          fullWidth
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('callBackFormDialog.notesPlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} color="error" variant="contained">
          {t('callBackFormDialog.cancel')}
        </Button>
        <Button
          disabled={!isFormValid}
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          {t('callBackFormDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
